import React from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { Link } from 'react-router-dom';
import { useLocalizedData } from '../hooks/useTranslatedContent';

export default function BlogsNews({ homePageData }) {
    const { getLocalized, getLocalizedArray } = useLocalizedData(homePageData);

    if (!homePageData) return null;

    const enterprisesTitle = getLocalized('enterprisesTitle');
    const enterprisesSubTitle = getLocalized('enterprisesSubTitle');
    const enterprisesBtnName = getLocalized('enterprisesBtnName');
    const enterprisesBtnLink = getLocalized('enterprisesBtnLink');
    const BlogsNewsdata = getLocalizedArray('enterprisesHome') || [];

    const responsive = {
        desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3, partialVisibilityGutter: 40 },
        tablet: { breakpoint: { max: 1024, min: 768 }, items: 2, partialVisibilityGutter: 30 },
        mobile: { breakpoint: { max: 768, min: 0 }, items: 1, arrows: false, partialVisibilityGutter: 20 },
    };

    const isExternalLink = (url) => typeof url === 'string' && /^https?:\/\//.test(url);

    const CustomArrow = ({ onClick, direction }) => (
        <button
            onClick={onClick}
            className={`absolute z-10 top-1/2 transform -translate-y-1/2 bg-white dark:bg-slate-800 p-2 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${
                direction === 'left' ? 'left-5 -ml-1' : 'right-5 -mr-1'
            }`}
            aria-label={direction === 'left' ? 'Previous' : 'Next'}
        >
            {direction === 'left' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            )}
        </button>
    );

    return (
        <div className="relative w-full py-16 overflow-hidden">
            <div className="absolute inset-0 bg-black opacity-10 pointer-events-none z-0" />
            <div className="container lg:py-8 text-center relative z-10">
                <h3 className="mb-4 md:text-2xl text-xl font-medium text-slate-600">
                    {enterprisesTitle || "Our Enterprises"}
                </h3>
                <p className="text-slate-500 max-w-xl mx-auto">
                    {enterprisesSubTitle || "Discover our latest enterprises and projects"}
                </p>

                <div className="mt-10 relative">
                    {BlogsNewsdata.length > 3 ? (
                        <Carousel
                            responsive={responsive}
                            infinite
                            autoPlay
                            autoPlaySpeed={5000}
                            arrows={true}
                            showDots={false}
                            itemClass="px-4"
                            ssr={true}
                            shouldResetAutoplay={false}
                            removeArrowOnDeviceType={['mobile', 'tablet']}
                            customLeftArrow={<CustomArrow direction="left" />}
                            customRightArrow={<CustomArrow direction="right" />}
                        >
                            {BlogsNewsdata.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col justify-between h-full max-w-xl mx-auto rounded-md shadow shadow-slate-200 dark:shadow-gray-800 overflow-hidden bg-white dark:bg-slate-900"
                                >
                                    <div className="relative">
                                        {item.img && (
                                            <img
                                                src={item.img}
                                                alt={item.title || 'enterprise image'}
                                                className="w-full h-48 object-cover"
                                            />
                                        )}
                                    </div>

                                    <div className="flex justify-center mt-4">
                                        <div className="w-8 h-8 object-contain">
                                            {item.iconLink ? (
                                                isExternalLink(item.iconLink) ? (
                                                    <a href={item.iconLink} target="_blank" rel="noopener noreferrer">
                                                        {item.icon && <img src={item.icon} alt="icon" className="w-8 h-8" />}
                                                    </a>
                                                ) : (
                                                    <Link to={item.iconLink}>
                                                        {item.icon && <img src={item.icon} alt="icon" className="w-8 h-8" />}
                                                    </Link>
                                                )
                                            ) : (
                                                item.icon && <img src={item.icon} alt="icon" className="w-8 h-8" />
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-6 flex flex-col flex-grow justify-between text-left">
                                        {isExternalLink(item.btnLink) ? (
                                            <a
                                                href={item.btnLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-lg font-medium hover:text-gray-800 dark:text-white dark:hover:text-gray-800 block"
                                            >
                                                {item.title || "Enterprise"}
                                            </a>
                                        ) : (
                                            <Link
                                                to={item.btnLink || '/enterprise-detail'}
                                                className="text-lg font-medium hover:text-gray-800 dark:text-white dark:hover:text-gray-800 block"
                                            >
                                                {item.title || "Enterprise"}
                                            </Link>
                                        )}

                                        <div className="mt-3 text-slate-500 dark:text-slate-400">
                                            <div dangerouslySetInnerHTML={{ __html: item.description || "Enterprise description" }} />
                                        </div>

                                        <div className="mt-5">
                                            {isExternalLink(item.btnLink) ? (
                                                <a
                                                    href={item.btnLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-block px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded transition duration-300"
                                                >
                                                    {item.btnName || 'Read More'}
                                                </a>
                                            ) : (
                                                <Link
                                                    to={item.btnLink || '/enterprise-detail'}
                                                    className="inline-block px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded transition duration-300"
                                                >
                                                    {item.btnName || 'Read More'}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Carousel>
                    ) : (
                        <div className="space-y-12">
                            {BlogsNewsdata.map((item, index) => {
                                const isEven = index % 2 === 0;
                                return (
                                    <div
                                        key={index}
                                        className={`flex flex-col md:flex-row ${!isEven ? 'md:flex-row-reverse' : ''} 
                                            items-center dark:bg-slate-900 rounded-md overflow-hidden 
                                            transition-all duration-300 hover:shadow-lg`}
                                    >
                                        <div className="w-full md:w-1/2 h-72 md:h-96">
                                            <img
                                                src={item.img || '/default-enterprise.jpg'}
                                                alt={item.title || 'Blog post'}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="w-full md:w-1/2 p-6">
                                            {item.icon && (
                                                <div className="mb-4">
                                                    {item.iconLink ? (
                                                        isExternalLink(item.iconLink) ? (
                                                            <a href={item.iconLink} target="_blank" rel="noopener noreferrer">
                                                                <img
                                                                    src={item.icon}
                                                                    alt="Icon"
                                                                    className="w-24 h-24 object-contain mx-auto md:mx-0"
                                                                />
                                                            </a>
                                                        ) : (
                                                            <Link to={item.iconLink}>
                                                                <img
                                                                    src={item.icon}
                                                                    alt="Icon"
                                                                    className="w-24 h-24 object-contain mx-auto md:mx-0"
                                                                />
                                                            </Link>
                                                        )
                                                    ) : (
                                                        <img
                                                            src={item.icon}
                                                            alt="Icon"
                                                            className="w-24 h-24 object-contain mx-auto md:mx-0"
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                                {item.title}
                                            </h3>
                                            <p
                                                className="text-slate-600 dark:text-slate-400 mt-3"
                                                dangerouslySetInnerHTML={{ __html: item.description }}
                                            />
                                            <div className="mt-5">
                                                {isExternalLink(item.btnLink) ? (
                                                    <a
                                                        href={item.btnLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex justify-center items-center text-center w-38 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors duration-300"
                                                    >
                                                        {item.btnName || 'Read More'}
                                                    </a>
                                                ) : (
                                                    <Link
                                                        to={item.btnLink || '#'}
                                                        className="inline-flex justify-center items-center text-center w-38 bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors duration-300"
                                                    >
                                                        {item.btnName || 'Read More'}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {enterprisesBtnName  && (
                    <div className="mt-8">
                        {isExternalLink(enterprisesBtnLink) ? (
                            <a
                                href={enterprisesBtnLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center bg-gray-800 hover:bg-gray-900 border-gray-700 hover:border-gray-900 text-white rounded-md"
                            >
                                {enterprisesBtnName}
                            </a>
                        ) : (
                            <Link
                                to={enterprisesBtnLink}
                                className="py-2 px-5 inline-block font-normal tracking-wide border align-middle transition duration-500 ease-in-out text-base text-center bg-gray-800 hover:bg-gray-900 border-gray-700 hover:border-gray-900 text-white rounded-md"
                            >
                                {enterprisesBtnName}
                            </Link>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
